import React from 'react'
import { useLayoutContext } from 'src/layouts/VerticalLayout'

import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Badge from '@mui/material/Badge'
import Button from '@mui/material/Button'
import UserIcon from 'src/components/ui/UserIcon'
import ProfileMenu from './components/ProfileDropdown'
import CommunicationWidgetMini from 'src/components/widgets/CommunicationWidget/Components/CommunicationWidgetMini'
import SearchModal from './components/SearchModal'
import { RingCentralIncomingCall } from 'src/components/widgets/RingCentralIncomingCall/RingCentralIncomingCall'
import useResponsive from 'src/hooks/useResponsive'
import { useOnlineState } from 'src/hooks/useOnlineState'
import { useCommunicationSelect } from 'src/hooks/useCommunication'
import { useSearchQuery } from 'src/hooks/useSearchQuery'
import { useTwilioActions, useTwilioSelect } from 'src/hooks/useTwilio'
import NotificationsMenu from './components/NotificationMenu'
import {
  useNotificationsAction,
  useNotificationsSelect,
} from 'src/hooks/useNotifications'
import { useNumbersActions, useNumbersSelect } from 'src/hooks/useNumbers'
import { Drivers } from 'src/services/accounts'
import { useAssistantAction } from 'src/hooks/useAssistant'
import { useRouterGlobalSearch } from 'src/hooks/router/useGlobalSearch'
import { useTranslation } from 'react-i18next'

const GlobalSearchComponent = () => {
  const { t } = useTranslation('common')
  const routerGlobalSearch = useRouterGlobalSearch()
  const { isMobile } = useResponsive()
  const { setQuery } = useSearchQuery()
  const notificationsCount = useNotificationsSelect.notificationsCount()
  const getNotificationsCount = useNotificationsAction.getNotificationsCount()
  const assistantOpenWindow = useAssistantAction.toogleWindow()
  const [anchorNotif, setAnchorNotif] = React.useState<null | HTMLElement>(null)

  React.useEffect(() => {
    getNotificationsCount({
      isRead: false,
    })
  }, [])

  const { setNavHandle } = useLayoutContext()

  const openDialogHandle = () => {
    routerGlobalSearch.open()
  }

  const manageNavigationHandle = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setNavHandle?.()
  }

  const handleOpenNotifications = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setAnchorNotif(e.currentTarget)
  }

  const handleCloseNotifMenu = () => {
    setAnchorNotif(null)
  }

  const twillioEnabled = useCommunicationSelect.twillioEnabled()
  const isOnline = useOnlineState({})

  const handleOpenPhoneModal = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()

    if (isOnline && twillioEnabled) {
      setQuery({
        modal: 'phone',
      })
    }
  }

  return (
    <>
      <AudioContextSnackbar />
      <Stack
        direction="row"
        alignItems="center"
        sx={{ userSelect: 'none' }}
        spacing={3}
      >
        <Stack
          flex={1}
          px={{ xs: 3, xl: 6 }}
          elevation={1}
          component={Paper}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            cursor: 'pointer',
            height: 64,
            borderRadius: '12px',
          }}
          onClick={openDialogHandle}
        >
          <Stack
            direction="row"
            color="grey.800"
            alignItems="center"
            justifyContent={isMobile ? 'space-between' : undefined}
            spacing={2}
            flex={isMobile ? 1 : 0}
          >
            {isMobile && (
              <IconButton
                size="small"
                color="inherit"
                onClick={manageNavigationHandle}
              >
                <UserIcon color="inherit" icon="fluent:navigation-16-filled" />
              </IconButton>
            )}
            <Stack direction="row" alignItems="center" columnGap={2}>
              <Stack
                direction="row"
                alignItems="center"
                color="grey.900"
                columnGap={2}
              >
                <UserIcon
                  fontSize={20}
                  color="inherit"
                  icon="material-symbols-light:search-rounded"
                />
                <Typography color={'grey.500'} noWrap fontSize={14}>
                  {t('searchHere')}
                </Typography>
              </Stack>

              {isMobile && (
                <IconButton
                  color="inherit"
                  onClick={handleOpenPhoneModal}
                  size="small"
                >
                  <UserIcon color="inherit" icon="gridicons:phone" />
                </IconButton>
              )}
            </Stack>
          </Stack>

          <Stack
            direction="row"
            sx={{ height: '100%' }}
            alignItems="center"
            columnGap={6}
          >
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                assistantOpenWindow()
              }}
            >
              <Stack spacing={1} direction={'row'} alignItems={'center'}>
                <UserIcon icon="mingcute:ai-fill" fontSize={14}></UserIcon>
                {t('askAi')}
              </Stack>
            </Button>
            <IconButton
              onClick={handleOpenNotifications}
              sx={{ color: 'grey.900' }}
            >
              <Badge badgeContent={notificationsCount} color="info">
                <UserIcon icon="material-symbols-light:notifications-outline" />
              </Badge>
            </IconButton>

            {!isMobile && <ProfileMenu />}
          </Stack>
        </Stack>
        <RingCentralIncomingCall />
        {!isMobile && <CommunicationWidgetMini />}
      </Stack>
      {Boolean(anchorNotif) && (
        <NotificationsMenu
          anchor={anchorNotif}
          handleClose={handleCloseNotifMenu}
        />
      )}
      {routerGlobalSearch.isOpen && (
        <SearchModal
          openDialog={routerGlobalSearch.isOpen}
          onClose={() => routerGlobalSearch.close()}
        />
      )}
    </>
  )
}

function AudioContextSnackbar() {
  // const [open, setOpen] = React.useState(false)
  const setupClient = useTwilioActions.setupClient()
  // const audio = React.useRef(new Audio(`/wheelSound.mp3`))
  const twillioDevice = useTwilioSelect.device()
  const resetTwillioDevice = useTwilioActions.resetClient()

  const accounts = useNumbersSelect.numbers()
  const getAccountsStatus = useNumbersActions.getNumbers.status()

  // const audioContext = new AudioContext()

  // const handleAccept = () => {
  //   audio.current.play()
  //   setupClient()

  //   // audioContext.resume().then(() => {
  //   //   // setOpen(false)
  //   //   // setupClient()
  //   // })
  // }

  React.useEffect(() => {
    const hasTwillioDriver = accounts.data.some(
      (acc) => acc.driver === Drivers.TWILIO
    )
    if (hasTwillioDriver && !twillioDevice) {
      setupClient()
    }

    if (!hasTwillioDriver && twillioDevice && getAccountsStatus.isFulfilled) {
      resetTwillioDevice()
    }
  }, [accounts.data.length, twillioDevice, getAccountsStatus.isFulfilled])

  // React.useEffect(() => {
  //   const handle = () => {
  //     handleAccept()
  //     window.document.body.removeEventListener('click', handle)
  //   }

  //   window.document.body.addEventListener('click', handle)

  //   return () => window.document.body.removeEventListener('click', handle)
  // }, [])

  // React.useEffect(() => {
  //   audio.current.play().catch(() => {
  //     setOpen(true)
  //   })
  // }, [audio])

  return <></>

  // return (
  //   <Snackbar
  //     open={open}
  //     anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
  //     message={
  //       <Box>
  //         <Typography color="inherit">
  //           To play sound on our site, your permission is required.
  //         </Typography>
  //         <a
  //           target="_blank"
  //           href="https://developer.chrome.com/blog/autoplay"
  //           style={{ color: '#fff', textDecoration: 'underline' }}
  //         >
  //           Learn More
  //         </a>
  //       </Box>
  //     }
  //     action={
  //       <Button color="primary" onClick={handleAccept} size="small">
  //         Accept
  //       </Button>
  //     }
  //   />
  // )
}

export default GlobalSearchComponent
